import { CardStatus, DEFAULT_CATEGORY_ID, QuickFilter } from '../../constants/app.constants';
import { createDefaultCategory, createDefaultUiFilter } from '../../utils/factories';
import {
  createSequentialLadderLevels,
  normalizeHierarchicalLadder,
} from '../../utils/ladder.helper';
import {
  PersistedCard,
  PersistedCategory,
  PersistedUiFilterState,
  PersistedUiState,
} from '../schemas/persisted-entities.model';
import { CurrentPersistedAppState } from '../schemas/persisted-app-state.model';

export interface NormalizedPersistedAppStateResult {
  state: CurrentPersistedAppState;
  wasNormalized: boolean;
}

export function normalizePersistedState(
  persistedState: CurrentPersistedAppState,
): NormalizedPersistedAppStateResult {
  const categories = normalizePersistedCategories(persistedState.categories);
  const cardsWithDefaults = normalizePersistedCards(persistedState.cards, categories);
  const cardsPreparedForLadder = normalizeStaleHiddenLadderPlacement(cardsWithDefaults);
  const ladderCards = cardsPreparedForLadder
    .filter((card) => card.showInLadder)
    .map((card) => ({
      ...card,
      priority: card.priority ?? 1,
    }));
  const ladderLevels = createSequentialLadderLevels(
    ladderCards,
    normalizeConfiguredLadderLevels(persistedState.ladder.levels),
  );
  const normalizedLadderCards = normalizeHierarchicalLadder(ladderCards, ladderLevels);
  const normalizedLadderCardsById = new Map(
    normalizedLadderCards.map((card) => [card.id, card] as const),
  );
  const normalizedCards = cardsPreparedForLadder.map((card) => {
    const normalizedLadderCard = normalizedLadderCardsById.get(card.id);

    return normalizedLadderCard
      ? {
          ...card,
          priority: normalizedLadderCard.priority,
          importance: normalizedLadderCard.importance,
          parentCardId: normalizedLadderCard.parentCardId,
        }
      : card;
  });
  const normalizedState: CurrentPersistedAppState = {
    ...persistedState,
    categories,
    cards: normalizedCards,
    ladder: {
      ...persistedState.ladder,
      levels: ladderLevels,
    },
    ui: normalizePersistedUiState(persistedState.ui),
  };

  return {
    state: normalizedState,
    wasNormalized: JSON.stringify(normalizedState) !== JSON.stringify(persistedState),
  };
}

function normalizePersistedCategories(categories: PersistedCategory[]): PersistedCategory[] {
  const defaultCategory = createDefaultCategory();
  const normalizedCategories = categories.map((category) => {
    const normalizedColor = isPersistedHexColor(category.color)
      ? category.color
      : defaultCategory.color;

    if (category.id !== DEFAULT_CATEGORY_ID) {
      return {
        ...category,
        color: normalizedColor,
      };
    }

    return {
      ...category,
      id: DEFAULT_CATEGORY_ID,
      name: typeof category.name === 'string' ? category.name : defaultCategory.name,
      color: normalizedColor,
      isSystem: true,
    };
  });

  if (normalizedCategories.some((category) => category.id === DEFAULT_CATEGORY_ID)) {
    return normalizedCategories;
  }

  return [...normalizedCategories, defaultCategory];
}

function normalizePersistedCards(
  cards: PersistedCard[],
  categories: PersistedCategory[],
): PersistedCard[] {
  const validCategoryIds = new Set(categories.map((category) => category.id));

  return cards.map((card) => ({
    ...card,
    categoryId: validCategoryIds.has(card.categoryId) ? card.categoryId : DEFAULT_CATEGORY_ID,
    ladderGroupColor: card.ladderGroupColor ?? null,
  }));
}

function normalizeStaleHiddenLadderPlacement(cards: PersistedCard[]): PersistedCard[] {
  return cards.map((card) => {
    if (card.showInLadder) {
      return card;
    }

    return {
      ...card,
      priority: null,
      importance: null,
    };
  });
}

function normalizeConfiguredLadderLevels(levels: number[]): number[] {
  return [...new Set(levels)]
    .filter((level) => Number.isInteger(level) && level > 0)
    .sort((a, b) => a - b);
}

function normalizePersistedUiState(uiState: PersistedUiState): PersistedUiState {
  const uiRecord = toRecord(uiState);

  return {
    ...uiRecord,
    ladder: normalizePersistedUiFilterState(uiRecord['ladder']),
    allCards: normalizePersistedUiFilterState(uiRecord['allCards']),
  } as PersistedUiState;
}

function normalizePersistedUiFilterState(candidate: unknown): PersistedUiFilterState {
  const defaultFilter = createDefaultUiFilter();
  const filterRecord = toRecord(candidate);
  const status = filterRecord['status'];
  const quickFilter = filterRecord['quickFilter'];
  const categoryId = filterRecord['categoryId'];

  return {
    ...filterRecord,
    textQuery: typeof filterRecord['textQuery'] === 'string' ? filterRecord['textQuery'] : defaultFilter.textQuery,
    status: isPersistedStatus(status) ? status : defaultFilter.status,
    categoryId: typeof categoryId === 'string' || categoryId === null ? categoryId : defaultFilter.categoryId,
    quickFilter: isQuickFilterValue(quickFilter) ? quickFilter : defaultFilter.quickFilter,
  };
}

function isPersistedStatus(value: unknown): value is PersistedUiFilterState['status'] {
  return value === 'ALL' || Object.values(CardStatus).includes(value as CardStatus);
}

function isQuickFilterValue(value: unknown): value is QuickFilter {
  return Object.values(QuickFilter).includes(value as QuickFilter);
}

function isPersistedHexColor(value: unknown): value is string {
  return typeof value === 'string' && /^#[0-9A-Fa-f]{6}$/.test(value);
}

function toRecord(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? value as Record<string, unknown> : {};
}